import { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Form, message, Card, Checkbox } from 'antd'
import { Formik } from 'formik'
import humps from 'humps'
import { useNavigate, useParams } from 'react-router-dom'
import startCase from 'lodash/startCase'
import get from 'lodash/get'
import noop from 'lodash/noop'
import { createUseStyles } from 'react-jss'
import { reportShowPath } from '../../lib/reports'

import { formElementPropsGenerator } from '../../lib/forms'
import { SubmitButton, Checkbox as FormikCheckbox } from '../Forms'
import api from '../../api'
import useQueryParams from '../../hooks/useQueryParams'
import QuestionForm from './QuestionForm'
import FormItem from './FormItem'
import TransmissionModal from './TransmissionModal'

import {
  formatInitialValues,
  formatSavedValues,
  getLabels,
  getPlaceholders,
  buildValidationSchema,
} from './form-utils'
import FormItems from '../Common/FormItems'

function filterCycle30Fields(formSections) {
  return formSections.filter(section => section.restrictedTo !== 'cycle_30')
}

const useStyles = createUseStyles({
  sectionCard: {
    marginTop: '16px',
    marginBottom: '16px',
  },
})

export default function ReportForm({
  report,
  isV2Report = false,
  onDataRefresh = noop,
  rawReport = null,
  showIssuesForm = true,
  issuesFormFields = {
    errorFields: [],
    observations: '',
  },
  onReportField = noop,
}) {
  const navigate = useNavigate()
  const { id } = useParams()
  const classes = useStyles()
  const [answeredQuestion, setAnsweredQuestion] = useState(false)
  const [filterFields, setFilterFields] = useState(report.filteredFields)
  const [query, , getPathWithQuery] = useQueryParams({})
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const {
    formSections: initialFormSections,
    fieldValues,
    critical,
    device,
    additionalQuestion,
    hasFile,
    version,
    hasLongCycleAndDeviceWithFullLength,
    suggestions,
  } = report
  const showQuestionForm = !hasFile && !answeredQuestion && additionalQuestion
  const formSections = filterFields
    ? filterCycle30Fields(initialFormSections)
    : initialFormSections

  const handleEdit = useCallback(
    async ({ critical: criticalField, ...values }) => {
      const reportValues = formatSavedValues(values, formSections)
      try {
        if (issuesFormFields.errorFields.length > 0) {
          // Send issues form fields
          await api.transmissions.reportExtractionError(
            rawReport?.pdfExtraction.id,
            issuesFormFields,
          )
        }
        if (filterFields) {
          await api.reports.turnOff(id)
        }

        if (report.version === 'v2' && !report.biobridgeReport) {
          await api.remoteChecks.createBiobridgeReport(id, {
            reportValues,
            critical: criticalField,
          })
        } else {
          await api.reports.updateReportForm(
            id,
            { reportValues, critical: criticalField },
            version,
          )
        }

        navigate(getPathWithQuery(reportShowPath(report), query))
        message.success('BioBridge report has been edited')
      } catch (e) {
        message.error(
          `There was an error editing a BioBridge report: ${e.message}`,
        )
      }
    },
    [
      formSections,
      issuesFormFields,
      filterFields,
      navigate,
      report,
      getPathWithQuery,
      query,
      rawReport,
      id,
      version,
    ],
  )

  const backToIndex = useCallback(() => {
    const path = `/practices/${report.practiceId}/reports?cycleId=${report.cycleId}`
    navigate(path)
    message.success('BioBridge report has been rejected')
  }, [navigate, report.practiceId, report.cycleId])

  const refetchTransmission = useCallback(async () => {
    try {
      await api.reports.getReportForm(report.id, report.version === 'v2')
      setIsModalVisible(true)
    } catch (err) {
      backToIndex()
    }
  }, [report.id, report.version, backToIndex])

  const handleQuestion = useCallback(
    async ({ question }) => {
      try {
        if (question === 'yes') {
          setAnsweredQuestion(true)
          return
        }

        if (question === 'no' && hasLongCycleAndDeviceWithFullLength) {
          setFilterFields(true)
          setAnsweredQuestion(true)
          return
        }

        if (question === 'no') {
          setIsProcessing(true)
          try {
            if (isV2Report) {
              const response = await api.transmissions.archive(
                report.transmissionId,
              )
              if (response.isArchived) {
                refetchTransmission()
              } else {
                backToIndex()
              }
            } else {
              await api.reports.turnOff(id, true)
              message.success('BioBridge report has been rejected')
              navigate('/unreviewed-reports')
            }
          } catch (e) {
            message.error(`Error archiving transmission: ${e.message}`)
          } finally {
            setIsProcessing(false)
          }
        }
      } catch (e) {
        message.error(`There was an error handling the response: ${e.message}`)
      }
    },
    [
      hasLongCycleAndDeviceWithFullLength,
      isV2Report,
      report.transmissionId,
      refetchTransmission,
      backToIndex,
      id,
      navigate,
    ],
  )

  if (showQuestionForm) {
    return (
      <>
        <QuestionForm
          additionalQuestion={additionalQuestion}
          handleQuestion={handleQuestion}
          disabled={isProcessing}
        />
        <TransmissionModal
          visible={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false)
            backToIndex()
          }}
          onConfirm={() => {
            setIsModalVisible(false)
            setAnsweredQuestion(true)
            onDataRefresh()
          }}
        />
      </>
    )
  }

  const validationSchema = buildValidationSchema(formSections)
  const formType = humps.camelize(
    get(device, 'deviceModel.deviceType.formType'),
  )
  const propsFor = formElementPropsGenerator({
    formName: 'report-edit-form',
    labels: { ...getLabels(formSections), critical: 'Requires Attention' },
    placeholders: getPlaceholders(formSections),
  })

  return (
    <Formik
      onSubmit={handleEdit}
      initialValues={{
        ...formatInitialValues(fieldValues, formSections, formType, rawReport),
        critical,
      }}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, isSubmitting, isValid, submitCount }) => (
        <Form onFinish={handleSubmit}>
          {formSections.map(section => (
            <Card
              type="inner"
              title={startCase(section.name)}
              key={section.name}
              className={classes.sectionCard}
              size="small"
            >
              <FormItems>
                {section.fields.map(field => {
                  const isParsed =
                    rawReport?.deviceReportMetadata?.[
                      humps.camelize(field.itemName)
                    ] !== null

                  const key = humps.camelize(field.itemName)
                  return (
                    <div key={field.itemId}>
                      <FormItem
                        propsFor={propsFor}
                        field={field}
                        formType={formType}
                        suggestions={
                          suggestions.find(
                            suggestion =>
                              suggestion.reportItemId === field.itemId,
                          )?.suggestions || []
                        }
                        layout="vertical"
                      />
                      {isParsed &&
                        showIssuesForm &&
                        !report.biobridgeReport && (
                          <Checkbox
                            checked={issuesFormFields.errorFields.includes(key)}
                            onClick={() => onReportField(field)}
                          >
                            Report autofill error
                          </Checkbox>
                        )}
                    </div>
                  )
                })}
              </FormItems>
            </Card>
          ))}
          <Card
            type="inner"
            title="Requires Attention"
            className={classes.sectionCard}
            size="small"
          >
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <FormikCheckbox {...propsFor('critical')} />
          </Card>
          <SubmitButton
            isSubmitting={isSubmitting}
            isValid={isValid}
            submitCount={submitCount}
            title="Save Report"
          />
        </Form>
      )}
    </Formik>
  )
}

ReportForm.propTypes = {
  report: PropTypes.shape().isRequired,
  isV2Report: PropTypes.bool,
  onDataRefresh: PropTypes.func,
  rawReport: PropTypes.shape(),
  issuesFormFields: PropTypes.shape(),
  onReportField: PropTypes.func,
  showIssuesForm: PropTypes.bool,
}
